import type { ChartData, StudyTemplateData } from '@charting-library'
import { z } from 'zod'

import {
  chartApi,
  type ChartResolution,
  type ChartSearchParams,
  type ChartStudy,
  type HistoryWindow,
  type PriceBasis,
  type StudyParams,
} from '@/features/trading-chart/api/chartApi'
import {
  historyMeta,
  toBarCandidates,
  toDatafeedConfig,
  toLayoutMeta,
  toLayoutWrite,
  toPaneSeries,
  toSearchResult,
  toSymbolInfo,
  toTradeSizeSeries,
} from '@/features/trading-chart/api/chartMappers'
import {
  chartContentSchema,
  chartDatafeedConfigSchema,
  chartHistorySchema,
  chartLayoutsSchema,
  chartSearchResultsSchema,
  chartSymbolSchema,
  paneSeriesSchema,
  studyTemplatesSchema,
  type ChartBar,
  type ChartDatafeedConfig,
  type ChartHistory,
  type ChartLayoutMeta,
  type ChartSearchResult,
  type ChartSymbol,
  type PaneSeries,
  type StudyTemplateMeta,
} from '@/schemas/trading-chart'

const serverTimeSchema = z.number().int()
const layoutIdSchema = z.number().int()

/**
 * The `/charts/*` reads and writes in the library's shapes: wire → mapper →
 * schema. Every read is validated here, once, so the datafeed and the
 * save/load adapter hand the library only what it was promised.
 */
export const chartService = {
  config: async (): Promise<ChartDatafeedConfig> =>
    chartDatafeedConfigSchema.parse(toDatafeedConfig(await chartApi.config())),

  symbol: async (symbol: string): Promise<ChartSymbol> =>
    chartSymbolSchema.parse(toSymbolInfo(await chartApi.symbol(symbol))),

  search: async (params: ChartSearchParams): Promise<ChartSearchResult[]> => {
    const page = await chartApi.search(params)
    return chartSearchResultsSchema.parse(page.rows.map(toSearchResult))
  },

  history: async (
    symbol: string,
    window: HistoryWindow,
    resolution?: ChartResolution,
    basis?: PriceBasis,
  ): Promise<ChartHistory> => {
    const series = await chartApi.history(symbol, window, resolution, basis)
    return chartHistorySchema.parse({ bars: toBarCandidates(series), meta: historyMeta(series) })
  },

  /**
   * The two newest bars at `resolution`, oldest first: the forming one (today's session so far,
   * or the minute in progress) and the one before it, whose last ticks a poll may not have seen
   * before the next bar opened. Empty for a symbol with none.
   */
  latestBars: async (
    symbol: string,
    resolution: ChartResolution,
    basis: PriceBasis,
  ): Promise<ChartBar[]> => {
    const { bars } = await chartService.history(symbol, { countback: 2 }, resolution, basis)
    return bars
  },

  /** One study pane over mm-be's default window, its latest 250 sessions. */
  study: async (study: ChartStudy, symbol: string, params: StudyParams = {}): Promise<PaneSeries> =>
    paneSeriesSchema.parse(toPaneSeries(await chartApi.study(study, symbol, params))),

  /**
   * The average trade size pane: the newest `sessions` daily bars' kitta per trade. Read here
   * rather than from the bars the chart draws, because the library's bar holds OHLCV only and the
   * trade count (`n`) cannot ride on it.
   */
  tradeSize: async (symbol: string, sessions: number): Promise<PaneSeries> =>
    paneSeriesSchema.parse(
      toTradeSizeSeries(await chartApi.history(symbol, { countback: sessions })),
    ),

  /** Epoch seconds, the unit the library's clock sync takes. */
  serverTime: async (): Promise<number> => serverTimeSchema.parse((await chartApi.time()).time),

  layouts: async (): Promise<ChartLayoutMeta[]> =>
    chartLayoutsSchema.parse((await chartApi.layouts()).rows.map(toLayoutMeta)),

  layoutContent: async (layoutId: number): Promise<string> =>
    chartContentSchema.parse((await chartApi.layout(layoutId)).content),

  /** A first save creates the layout and an id-carrying one overwrites it; resolves the id. */
  saveLayout: async (chart: ChartData, layoutId: number | null): Promise<number> => {
    const body = toLayoutWrite(chart)
    const saved =
      layoutId === null
        ? await chartApi.createLayout(body)
        : await chartApi.updateLayout(layoutId, body)
    return layoutIdSchema.parse(saved.id)
  },

  deleteLayout: async (layoutId: number): Promise<void> => {
    await chartApi.deleteLayout(layoutId)
  },

  studyTemplates: async (): Promise<StudyTemplateMeta[]> =>
    studyTemplatesSchema.parse((await chartApi.studyTemplates()).rows),

  studyTemplateContent: async (name: string): Promise<string> =>
    chartContentSchema.parse((await chartApi.studyTemplate(name)).content),

  saveStudyTemplate: async ({ name, content }: StudyTemplateData): Promise<void> => {
    await chartApi.saveStudyTemplate({ name, content })
  },

  deleteStudyTemplate: async (name: string): Promise<void> => {
    await chartApi.deleteStudyTemplate(name)
  },
}
